import React, { useState, useRef, useEffect } from 'react';

const DatePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select Date", 
  className = "form-control",
  disabled = false,
  required = false,
  maxDate = null,
  minDate = null
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    if (!date) return placeholder;
    const d = new Date(date);
    return d.toLocaleDateString('en-GB');
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    // Fill remaining cells to complete the grid (6 rows × 7 days = 42 cells)
    while (days.length < 42) {
      days.push(null);
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    const formattedDate = formatDate(date);
    onChange(formattedDate);
    setShowCalendar(false);
  };

  const isDateDisabled = (date) => {
    if (!date) return false;
    
    // Normalize dates to compare only date part (ignore time)
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      const maxDateOnly = new Date(maxDateObj.getFullYear(), maxDateObj.getMonth(), maxDateObj.getDate());
      if (dateOnly > maxDateOnly) return true;
    }
    
    if (minDate) {
      const minDateObj = new Date(minDate);
      const minDateOnly = new Date(minDateObj.getFullYear(), minDateObj.getMonth(), minDateObj.getDate());
      if (dateOnly < minDateOnly) return true;
    }
    
    return false;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date && 
           date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    if (!value || !date) return false;
    const selectedDate = new Date(value);
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  return (
    <div className="position-relative" ref={datePickerRef}>
      <div 
        className={`${className} d-flex justify-content-between align-items-center ${disabled ? 'disabled' : ''}`}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '0.875rem', padding: '0.375rem 0.75rem', gap: '8px' }}
        onClick={() => !disabled && setShowCalendar(!showCalendar)}
      >
        <span className={value ? 'text-dark fw-normal' : 'text-muted'}>
          {formatDisplayDate(value)}
        </span>
        <i className="fas fa-calendar-alt" style={{ color: '#6c757d', fontSize: '0.8rem' }}></i>
      </div>

      {showCalendar && (
        <div 
          className="position-absolute bg-white border rounded shadow-lg p-2" 
          style={{ 
            top: '100%', 
            right: 0,
            left: 'auto',
            zIndex: 9999, 
            minWidth: '260px',
            marginTop: '4px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            overflow: 'visible'
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Calendar Header */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button 
              type="button"
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
              onClick={() => navigateMonth(-1)}
            >
              ‹
            </button>
            <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '0.875rem', letterSpacing: '0.3px' }}>
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h6>
            <button 
              type="button"
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
              onClick={() => navigateMonth(1)}
            >
              ›
            </button>
          </div>

          {/* Days of Week */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', marginBottom: '6px' }}>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="text-center py-1" style={{ fontSize: '0.7rem', fontWeight: '700', color: '#495057', letterSpacing: '0.3px' }}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
            {getDaysInMonth(currentMonth).map((date, index) => (
              <div key={index} style={{ aspectRatio: '1', minHeight: '30px' }}>
                {date ? (
                  <button
                    type="button"
                    className={`btn btn-sm w-100 h-100 d-flex align-items-center justify-content-center ${
                      isDateDisabled(date) ? 'btn-outline-light text-muted' :
                      isSelected(date) ? 'btn-primary text-white' :
                      isToday(date) ? 'btn-outline-primary' :
                      'btn-outline-light text-dark'
                    }`}
                    onClick={() => handleDateClick(date)}
                    disabled={isDateDisabled(date)}
                    style={{ 
                      fontSize: '0.75rem',
                      fontWeight: isSelected(date) || isToday(date) ? '600' : '400',
                      cursor: isDateDisabled(date) ? 'not-allowed' : 'pointer',
                      border: isSelected(date) ? '2px solid #0d6efd' : '1px solid #dee2e6',
                      padding: '2px'
                    }}
                  >
                    {date.getDate()}
                  </button>
                ) : (
                  <div></div>
                )}
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="mt-2 d-flex gap-1">
            <button 
              type="button"
              className="btn btn-sm btn-outline-primary flex-fill"
              style={{ fontSize: '0.75rem', padding: '0.25rem' }}
              onClick={() => handleDateClick(new Date())}
              disabled={isDateDisabled(new Date())}
            >
              <i className="bi bi-calendar-check me-1"></i>Today
            </button>
            <button 
              type="button"
              className="btn btn-sm btn-outline-secondary flex-fill"
              style={{ fontSize: '0.75rem', padding: '0.25rem' }}
              onClick={() => setShowCalendar(false)}
            >
              <i className="bi bi-x-lg me-1"></i>Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;